import { useEffect, useRef, useState } from "react";

function useRevealOnScroll(ref) {
  useEffect(() => {
    const elements = ref.current?.querySelectorAll("[data-reveal]");
    if (!elements) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.delay || "0";
            setTimeout(() => {
              el.style.opacity = "1";
              el.style.transform = "translateY(0)";
            }, parseFloat(delay));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 },
    );

    elements.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(20px)";
      el.style.transition = "opacity 0.75s ease, transform 0.75s ease";
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
}

// ─── Replace these with real testimonials when ready ───────────────────────
const testimonials = [
  {
    id: 1,
    quote:
      "Something shifted in that session that I had been trying to shift for years. The music, the space, the way Noah listened — it was unlike anything I'd experienced in therapy.",
    name: "— Placeholder name",
    context: "One-on-one session",
  },
  {
    id: 2,
    quote:
      "I came in not knowing what to expect. I left feeling like I had found a part of myself I didn't know was missing. The live music made everything feel safe enough to actually go there.",
    name: "— Placeholder name",
    context: "Group ceremony, Amsterdam",
  },
  {
    id: 3,
    quote:
      "Noah has a rare gift — he holds the space with so much steadiness that you feel completely free to fall apart if you need to. And the music is extraordinary. It moves through you.",
    name: "— Placeholder name",
    context: "4-session package",
  },
  {
    id: 4,
    quote:
      "I've been to sound baths before and this was completely different. There's a depth and intention to what Noah does — you can feel the years of training behind every note.",
    name: "— Placeholder name",
    context: "Sound journey, retreat",
  },
  {
    id: 5,
    quote:
      "The preparation and integration work made all the difference. It wasn't just a ceremony — it was a whole arc. I'm still working with what came up and that feels exactly right.",
    name: "— Placeholder name",
    context: "Ceremonial truffle session",
  },
];
// ───────────────────────────────────────────────────────────────────────────

function StarIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function TestimonialCard({ testimonial, index }) {
  return (
    <div
      data-reveal
      data-delay={`${index * 100}`}
      className="flex flex-col bg-cream border border-sand-dark p-8 lg:p-10 hover:bg-sand transition-colors duration-300"
    >
      {/* Stars */}
      <div className="flex gap-1 mb-6">
        {[...Array(5)].map((_, i) => (
          <StarIcon key={i} className="w-3.5 h-3.5 text-earth-mid" />
        ))}
      </div>

      {/* Quote mark */}
      <span
        className="font-cormorant text-earth-light/30 leading-none mb-2 select-none"
        style={{ fontSize: "5rem", lineHeight: 0.8 }}
        aria-hidden="true"
      >
        "
      </span>

      {/* Quote text */}
      <p
        className="font-cormorant italic font-light text-ink leading-[1.65] flex-grow mb-8"
        style={{ fontSize: "clamp(1.05rem, 1.3vw, 1.2rem)" }}
      >
        {testimonial.quote}
      </p>

      {/* Attribution */}
      <div className="border-t border-sand-dark pt-5">
        <p className="font-jost font-light text-stone text-[0.82rem] mb-1">
          {testimonial.name}
        </p>
        <p className="font-jost font-light text-earth/60 text-[0.72rem] tracking-[0.1em] uppercase">
          {testimonial.context}
        </p>
      </div>
    </div>
  );
}

export const TestimonialsContent = () => {
  const sectionRef = useRef(null);
  useRevealOnScroll(sectionRef);

  // For mobile: simple swipe-friendly horizontal scroll
  // On desktop: 3-column grid showing first 3, with "see more" expanding to all 5
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? testimonials : testimonials.slice(0, 3);

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="bg-sand py-24 lg:py-32 px-8 lg:px-16"
    >
      <div className="max-w-7xl mx-auto">
        {/* ── Hipsy trust strip ── */}
        <a
          href="https://www.hipsy.nl"
          target="_blank"
          rel="noopener noreferrer"
          data-reveal
          data-delay="0"
          className="inline-flex items-center gap-3 mb-16 lg:mb-20 group"
          aria-label="View Noah's reviews on Hipsy"
        >
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <StarIcon key={i} className="w-4 h-4 text-earth-mid" />
            ))}
          </div>
          <span className="font-jost font-light text-stone text-sm tracking-wide">
            5-star rated on Hipsy
          </span>
          <span className="font-jost font-light text-earth-light/60 text-[0.72rem] tracking-[0.1em] uppercase">
            · View reviews
          </span>
          <span
            className="text-earth-light/40 group-hover:text-earth transition-colors duration-300 text-xs"
            aria-hidden="true"
          >
            ↗
          </span>
        </a>

        {/* ── Section header ── */}
        <div className="mb-12 lg:mb-16">
          <div className="flex items-baseline gap-6 flex-wrap">
            <p
              data-reveal
              data-delay="60"
              className="text-[0.65rem] tracking-[0.3em] uppercase text-earth-light font-jost font-light whitespace-nowrap"
            >
              What people say
            </p>
            <h2
              data-reveal
              data-delay="120"
              className="font-cormorant font-light text-ink leading-[1.1]"
              style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.5rem)" }}
            >
              Voices from the <em className="italic text-earth">circle</em>
            </h2>
          </div>
        </div>

        {/* ── Testimonials grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-sand-dark">
          {visible.map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} index={i} />
          ))}
        </div>

        {/* ── Show more / less toggle ── */}
        {testimonials.length > 3 && (
          <div
            data-reveal
            data-delay="300"
            className="mt-10 flex justify-center"
          >
            <button
              onClick={() => setShowAll((v) => !v)}
              className="font-jost font-light text-[0.72rem] tracking-[0.18em] uppercase text-stone border-b border-stone/30 pb-0.5 hover:text-earth hover:border-earth transition-all duration-300"
            >
              {showAll ? "Show less" : `Read more voices`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
